import React, { useState, useContext } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faPaperPlane, faUsers } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { AppContext } from "../context/appContext";
import { useNavigate } from "react-router-dom";
import * as func from "../../api/apiHelper";
import * as urls from "../../api/urlHelper";
import AsyncSelect from "react-select/async";
import { MST_MODEL_NAMES } from "../../config/enum";
import { createLoadOptions } from "../../utilities";

const inputCls =
  "w-full px-3 mt-1 text-sm py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500";

const AddMatchSeeker = () => {
  const navigate = useNavigate();
  const { matchseekerMasterData } = useContext(AppContext);

  const [step, setStep] = useState(1);
  const [step1Data, setStep1Data] = useState({ phoneNumber: "", countryCode: "+91", type: "matchseeker" });
  const [otpTokenData, setOtpTokenData] = useState({});
  const [userIdToken, setUserIdToken] = useState({});
  const [otpMessage, setOtpMessage] = useState("");
  const [disableSendOtp, setDisableSendOtp] = useState(false);
  const [otp, setOtp] = useState(null);

  const [userData, setUserData] = useState({
    fullName: "",
    emailId: "",
    caste: "",
    subCaste: "",
    religion: "",
    matchMakerPreferences: { religion: "", caste: "", residentLocation: "" },
  });

  const handleSendOtp = async () => {
    setDisableSendOtp(true);
    const sendOtpApiresponse = await func.post(urls.VERIFY_USER_URL, step1Data);
    if (sendOtpApiresponse.status === 200 && sendOtpApiresponse.statusText === "OK") {
      toast.success(`Otp sent successfully to ${step1Data.phoneNumber}`);
      toast.info(sendOtpApiresponse.data.otp);
      setOtpTokenData(sendOtpApiresponse.data);
      setOtpMessage(`Enter the OTP sent to the number: ${step1Data.phoneNumber}`);
      setStep(2);
    } else {
      toast.warn(sendOtpApiresponse);
      setStep(1);
      setOtp(null);
    }
    setDisableSendOtp(false);
  };

  const handleVerifyOtp = async () => {
    const verifyOtpApiResponse = await func.post(urls.VERIFY_OTP, {
      hashedOtpToken: otpTokenData.hashedOtpToken,
      otp,
    });
    if (
      (verifyOtpApiResponse.status === 200 && verifyOtpApiResponse.statusText === "OK") ||
      (verifyOtpApiResponse.status === 201 && verifyOtpApiResponse.statusText === "Created")
    ) {
      setUserIdToken(verifyOtpApiResponse.data);
      toast.success("Otp Verified Successfully");
      setOtp(Array(4).fill(""));
      setStep(3);
    } else {
      toast.warn(verifyOtpApiResponse);
      setOtp(null);
      setStep(1);
    }
  };

  const handleSaveUserData = async () => {
    if (!userIdToken.token) { console.error("Authorization token is missing"); return; }
    const { fullName, emailId } = userData;
    if (!fullName) { toast.warn("All user fields must be filled"); return; }
    if (!fullName.trim()) { toast.warn("All user fields must be filled with valid input"); return; }
    if (fullName.trim().length < 3) { toast.warn("Full name must be at least 3 characters long"); return; }
    if (!/^[a-zA-Z\s'-]+$/.test(fullName.trim())) {
      toast.warn("Full name can only contain letters, spaces, apostrophes, and hyphens"); return;
    }
    if (emailId && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(emailId.trim())) {
      toast.warn("Please provide a valid email address"); return;
    }
    if (emailId && emailId.trim().length > 40) { toast.warn("Only 40 Characters allowed in the email id"); return; }

    const response = await func.post(urls.CREATE_MATCHSEEKER_URL, {
      ...userData,
      caste: userData.caste?.value || "",
      subCaste: userData.subCaste?.value || "",
      religion: userData.religion?.value || "",
      matchMakerPreferences: {
        religion: userData.matchMakerPreferences.religion?.value || "",
        caste: userData.matchMakerPreferences.caste?.value || "",
        residentLocation: userData.matchMakerPreferences.residentLocation?.value || "",
      },
      emailId: emailId ? emailId.toLowerCase() : null,
      userId: userIdToken.token,
    });

    if (response.status === 200 || response.status === 201) {
      toast.success("Match Seeker Created Successfully");
      navigate("/user-management/match-seekers/list");
    } else {
      toast.warn(response);
    }
  };

  return (
    <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => navigate(-1)}
            className="w-8 h-8 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-lg transition text-white"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="text-sm" />
          </button>
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faUsers} className="text-white text-lg" />
            <h1 className="text-lg font-semibold text-white">Add Match Seeker</h1>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
          {/* Step 1 & 2: Mobile + OTP */}
          <div>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
              Phone Verification
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <label className="block text-sm font-medium text-gray-700">Mobile Number</label>
                <input
                  type="text"
                  value={step1Data.phoneNumber}
                  disabled={step > 2}
                  onChange={(e) => { setStep1Data({ ...step1Data, phoneNumber: e.target.value }); setStep(1); }}
                  onInput={(e) => {
                    let value = e.target.value.replace(/\D/g, "").slice(0, 10);
                    if (/^[0-5]/.test(value)) value = "";
                    e.target.value = value;
                  }}
                  className={`${inputCls} ${step > 2 ? "bg-gray-50" : ""}`}
                  placeholder="Enter Mobile Number"
                />
              </div>

              {step1Data.phoneNumber.length === 10 && step === 1 && (
                <div className="flex items-end">
                  <button
                    disabled={disableSendOtp}
                    onClick={handleSendOtp}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm rounded-md transition"
                  >
                    Send OTP
                  </button>
                </div>
              )}

              {step1Data.phoneNumber.length === 10 && step === 2 && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Enter OTP</label>
                    <input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      onInput={(e) => { e.target.value = e.target.value.replace(/\D/g, "").slice(0, 4); }}
                      className={inputCls}
                      placeholder="Enter OTP"
                    />
                    {otpMessage && <p className="mt-1 text-xs text-green-600">{otpMessage}</p>}
                  </div>
                  <div className="flex items-end gap-2">
                    {otp?.length === 4 && (
                      <button
                        onClick={handleVerifyOtp}
                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-md transition"
                      >
                        Verify OTP
                      </button>
                    )}
                    <button
                      onClick={handleSendOtp}
                      className="px-4 py-2 border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm rounded-md transition"
                    >
                      Resend
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Step 3: User Details */}
          {step === 3 && (
            <>
              <div>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                  Personal Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={userData.fullName}
                      onChange={(e) => setUserData({ ...userData, fullName: e.target.value })}
                      onInput={(e) => (e.target.value = e.target.value.slice(0, 30))}
                      className={inputCls}
                      placeholder="Full Name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      value={userData.emailId}
                      onChange={(e) => setUserData({ ...userData, emailId: e.target.value })}
                      className={inputCls}
                      placeholder="Email"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Religion <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-1">
                      <AsyncSelect
                        placeholder="Select Religion"
                        isClearable
                        defaultOptions={matchseekerMasterData["mst-religion"]?.slice(0, 5)?.map((r) => ({
                          value: r._id, label: r.religion,
                        }))}
                        cacheOptions
                        loadOptions={createLoadOptions(MST_MODEL_NAMES[9])}
                        value={userData.religion}
                        onChange={(option) => setUserData({ ...userData, religion: option, caste: "" })}
                      />
                    </div>
                  </div>

                  {userData.religion ? (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Caste</label>
                        <div className="mt-1">
                          <AsyncSelect
                            placeholder="Select Caste"
                            isClearable
                            cacheOptions
                            isDisabled={!userData.religion}
                            loadOptions={createLoadOptions(MST_MODEL_NAMES[10], 50, userData.religion?.value)}
                            defaultOptions={matchseekerMasterData["mst-caste"]
                              ?.slice(0, 10)
                              ?.filter((each) => each.religionId === userData?.religion?.value)
                              ?.map((r) => ({ value: r._id, label: r.caste }))}
                            value={userData.caste}
                            onChange={(option) => setUserData({ ...userData, caste: option })}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Sub Caste</label>
                        <input
                          value={userData.subCaste}
                          onChange={(e) => setUserData({ ...userData, subCaste: e.target.value })}
                          onInput={(e) => {
                            let value = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                            if (value.trim() === "") value = "";
                            e.target.value = value;
                          }}
                          type="text"
                          className={inputCls}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Caste</label>
                        <select value={userData.caste} disabled className={`${inputCls} bg-gray-50`}>
                          <option value="">Select Caste</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Sub Caste</label>
                        <input value={userData.subCaste} type="text" disabled className={`${inputCls} bg-gray-50`} />
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
                  Preferences
                </h3>
                <p className="text-xs text-gray-500 mb-3">Select preferences to scout profiles</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Religion</label>
                    <div className="mt-1">
                      <AsyncSelect
                        placeholder="Select Religion"
                        isClearable
                        defaultOptions={matchseekerMasterData["mst-religion"]?.slice(0, 5)?.map((r) => ({
                          value: r._id, label: r.religion,
                        }))}
                        cacheOptions
                        loadOptions={createLoadOptions(MST_MODEL_NAMES[9])}
                        value={userData.matchMakerPreferences.religion}
                        onChange={(e) =>
                          setUserData({
                            ...userData,
                            matchMakerPreferences: { ...userData.matchMakerPreferences, religion: e, caste: "" },
                          })
                        }
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Caste</label>
                    <div className="mt-1">
                      <AsyncSelect
                        placeholder="Select Caste"
                        isClearable
                        cacheOptions
                        isDisabled={!userData.matchMakerPreferences.religion}
                        loadOptions={createLoadOptions(MST_MODEL_NAMES[10], 50, userData.matchMakerPreferences.religion?.value)}
                        defaultOptions={matchseekerMasterData["mst-caste"]
                          ?.slice(0, 10)
                          ?.filter((each) => each.religionId === userData.matchMakerPreferences.religion?.value)
                          ?.map((r) => ({ value: r._id, label: r.caste }))}
                        value={userData.matchMakerPreferences.caste}
                        onChange={(e) =>
                          setUserData({
                            ...userData,
                            matchMakerPreferences: { ...userData.matchMakerPreferences, caste: e },
                          })
                        }
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Resident Location</label>
                    <div className="mt-1">
                      <AsyncSelect
                        placeholder="Select Location"
                        isClearable
                        cacheOptions
                        loadOptions={createLoadOptions(MST_MODEL_NAMES[19])}
                        defaultOptions={matchseekerMasterData["mst-location"]?.slice(0, 5)?.map((r) => ({
                          value: r._id, label: r.city?.split(",")[0],
                        }))}
                        value={userData.matchMakerPreferences.residentLocation}
                        onChange={(e) =>
                          setUserData({
                            ...userData,
                            matchMakerPreferences: { ...userData.matchMakerPreferences, residentLocation: e },
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-xs text-gray-500">
                <span className="text-red-500">*</span> Represents required fields
              </p>

              <div className="flex justify-end">
                <button
                  onClick={handleSaveUserData}
                  className="flex items-center gap-2 px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-sm font-medium transition"
                >
                  <FontAwesomeIcon icon={faPaperPlane} />
                  Submit
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddMatchSeeker;
